import { useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  Building2,
  LayoutDashboard,
  Home,
  MessageSquare,
  BarChart3,
  Settings,
  LogOut,
  ExternalLink,
  Menu,
  X,
  LifeBuoy,
  ShieldCheck,
  Users,
  FileText
} from 'lucide-react';
import { useState } from 'react';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: Home, label: 'Properties', path: '/dashboard/properties' },
  { icon: MessageSquare, label: 'Enquiries', path: '/dashboard/enquiries' },
  { icon: Users, label: 'Our Team', path: '/dashboard/team' },
  { icon: FileText, label: 'About Page', path: '/dashboard/about-editor' },
  { icon: ShieldCheck, label: 'Reports', path: '/dashboard/reports' },
  { icon: LifeBuoy, label: 'Support Tickets', path: '/dashboard/tickets' },
  { icon: BarChart3, label: 'Analytics', path: '/dashboard/analytics' },
  { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
  { icon: Building2, label: 'Subscription', path: '/dashboard/subscription' },
];

export default function DashboardLayout() {
  const { agency, loading, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-50 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="w-12 h-12 text-sky-500 animate-pulse mx-auto mb-4" />
          <p className="text-navy-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!agency) return null;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-navy-50" data-testid="dashboard-layout">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-navy-200 px-4 py-3 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2">
          <Building2 className="w-7 h-7 text-sky-500" />
          <span className="font-heading font-bold text-navy-900">EstateCloud</span>
        </Link>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 bottom-0 w-64 bg-white border-r border-navy-200 z-40 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-navy-100">
            <Link to="/dashboard" className="flex items-center gap-2" data-testid="dashboard-logo">
              <Building2 className="w-8 h-8 text-sky-500" />
              <span className="text-xl font-heading font-bold text-navy-900">EstateCloud</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path ||
                (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive
                    ? 'bg-sky-50 text-sky-600'
                    : 'text-navy-600 hover:bg-navy-50'
                    }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Agency Info */}
          <div className="p-4 border-t border-navy-100">
            <div className="bg-navy-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-navy-600 mb-1">Your Website</p>
              <Link
                to={`/agency/${agency.slug}`}
                target="_blank"
                className="flex items-center gap-2 text-sky-500 hover:text-sky-600 font-medium text-sm"
                data-testid="view-website-link"
              >
                <span className="truncate">/{agency.slug}</span>
                <ExternalLink className="w-4 h-4 flex-shrink-0" />
              </Link>
            </div>
            <Button
              variant="outline"
              className="w-full justify-start text-navy-600 hover:text-navy-900"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}
